import React from "react";
import { FormHelperText } from "@mui/material";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  MAX_RECEIPT_AMOUNT_DECIMALS,
  MAX_RECEIPT_AMOUNT_DIGITS,
  MAX_RECEIPT_NUMBER_LENGTH,
  receiptUploadSchema,
  type ReceiptUploadSchema,
} from "./receiptUploadSchema";

interface ReceiptUploadDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onSubmit?: (payload: {
    receiptNumber: string;
    receiptDate: string;
    receiptAmount: string;
    receiptFile: File;
  }) => void;
  onDocumentUpload?: (file: File) => void;
  isUploading?: boolean;
  isSubmitting?: boolean;
}

const EMPTY: UploadEntry[] = [];

const F = {
  RECEIPT_NUMBER: "receiptNumber",
  RECEIPT_DATE: "receiptDate",
  RECEIPT_AMOUNT: "receiptAmount",
} as const;

export const sanitizeReceiptNumber = (input?: string) =>
  (input ?? "").replace(/[^A-Za-z0-9]/g, "").slice(0, MAX_RECEIPT_NUMBER_LENGTH);

const toISODate = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const ReceiptUploadDrawer: React.FC<ReceiptUploadDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onDocumentUpload,
  isUploading,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [fileError, setFileError] = React.useState<string | undefined>(undefined);

  const schema = React.useMemo(() => receiptUploadSchema(t), [t]);

  const form = useForm<ReceiptUploadSchema>({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: {
      receiptNumber: "",
      receiptDate: "",
      receiptAmount: "",
    },
  });

  const { handleSubmit, reset, setValue } = form;

  const receiptFiles = useUploadStore((s) => s.filesByKey.licenseReceiptAttachment ?? EMPTY);
  const isReceiptUploadInFlight = useUploadStore((s) =>
    s.isUploadInFlight("licenseReceiptAttachment"),
  );
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);
  const validReceiptFiles = receiptFiles.filter((file): file is File => file instanceof File);

  React.useEffect(() => {
    if (validReceiptFiles.length !== receiptFiles.length) {
      if (validReceiptFiles.length > 0) {
        setFilesForKey("licenseReceiptAttachment", validReceiptFiles);
      } else {
        clearFilesForKey("licenseReceiptAttachment");
      }
    }
  }, [clearFilesForKey, receiptFiles, setFilesForKey, validReceiptFiles]);

  const fileProfiles = useFileUploadProfiles();
  const receiptProfile = fileProfiles.sideDrawerApprovalFile;

  const allowedExtensions = receiptProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const resetForm = React.useCallback(() => {
    reset({
      receiptNumber: "",
      receiptDate: "",
      receiptAmount: "",
    });
    setFileError(undefined);
    clearFilesForKey("licenseReceiptAttachment");
  }, [clearFilesForKey, reset]);

  const prevOpenRef = React.useRef(isOpen);
  React.useEffect(() => {
    if (prevOpenRef.current && !isOpen) {
      resetForm();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, resetForm]);

  const handleClose = () => {
    onClose("receiptUploadDrawer");
    resetForm();
  };

  const handleCancel = () => {
    onClose("receiptUploadDrawer");
    resetForm();
  };

  const handleUpload = (newFiles: File[]) => {
    if (newFiles.length > 1) return;
    const file = newFiles[0];
    if (!file) return;

    setFilesForKey("licenseReceiptAttachment", [file]);
    setFileError(undefined);
    onDocumentUpload?.(file);
  };

  const handleDateFieldChange = (value: unknown) => {
    const v = value as unknown;
    if (v instanceof Date && !Number.isNaN(v.getTime())) {
      setValue(F.RECEIPT_DATE, toISODate(v), { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(F.RECEIPT_DATE, "", { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleSubmitForm = (data: ReceiptUploadSchema) => {
    const selectedFile = validReceiptFiles[0];
    if (!selectedFile) {
      setFileError(
        t("licensing.form-validation.mandatory", {
          label: t("licensing.upload-receipt"),
        }),
      );
      return;
    }

    onSubmit?.({
      receiptNumber: data.receiptNumber.trim(),
      receiptDate: data.receiptDate,
      receiptAmount: data.receiptAmount.trim(),
      receiptFile: selectedFile,
    });
  };

  const handleDrawerSubmit = () => {
    if (isUploading || isReceiptUploadInFlight) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    void handleSubmit(handleSubmitForm)();
  };

  if (!isOpen) return null;

  return (
    <div data-testid="receipt-upload-drawer-form" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t("licensing.receipt-information-title")}
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t("common.submit")}
        cancelLabel={t("common.cancel")}
        onSubmit={handleDrawerSubmit}
        isSubmitting={Boolean(isSubmitting)}
      >
        <div className="flex-1 w-[400px] py-2">
          <div className="flex flex-col gap-2 px-6">
            <FormProvider {...form}>
              <FormItem<ReceiptUploadSchema, typeof F.RECEIPT_NUMBER>
                required
                name={F.RECEIPT_NUMBER}
                type="text"
                label={t("licensing.receipt-number")}
                testId="receipt-number"
                maxChars={MAX_RECEIPT_NUMBER_LENGTH}
                maxCharsCustomError={t(
                  "licensing.form-validation.max-length-characters-and-digits",
                  {
                    label: t("licensing.receipt-number"),
                    max: MAX_RECEIPT_NUMBER_LENGTH,
                  },
                )}
                formatOnChange={(e) => {
                  const input = e.target.value;
                  const sanitized = sanitizeReceiptNumber(input);

                  if (sanitized !== input) {
                    e.target.value = sanitized;
                  }
                }}
              />

              <FormItem<ReceiptUploadSchema, typeof F.RECEIPT_DATE>
                required
                name={F.RECEIPT_DATE}
                type="date-picker"
                label={t("licensing.receipt-date")}
                timePicker={false}
                testId="receipt-date"
                dateConstraint="not-future"
                onFieldChange={(_, value) => handleDateFieldChange(value)}
              />

              <FormItem<ReceiptUploadSchema, typeof F.RECEIPT_AMOUNT>
                required
                name={F.RECEIPT_AMOUNT}
                type="number"
                label={t("licensing.receipt-amount")}
                testId="receipt-amount"
                maxDigits={MAX_RECEIPT_AMOUNT_DIGITS}
                decimals={MAX_RECEIPT_AMOUNT_DECIMALS}
              />
            </FormProvider>

            <DocumentUpload
              label={t("licensing.upload-receipt")}
              testId="license-receipt-attachment-upload"
              name="licenseReceiptAttachment"
              maxFiles={1}
              allowedExtensions={allowedExtensions}
              onUpload={handleUpload}
              conditions={receiptProfile}
              files={validReceiptFiles}
              setFiles={(next) => {
                setFilesForKey("licenseReceiptAttachment", next);
                if (next.length > 0) {
                  setFileError(undefined);
                }
              }}
              uploadDescription={t("upload.dragOrDropDescription")}
            />

            {fileError && (
              <FormHelperText sx={{ color: "error.main", m: 0 }}>{fileError}</FormHelperText>
            )}

            {validReceiptFiles[0] && (
              <SingleFile
                file={validReceiptFiles[0]}
                fieldName="licenseReceiptAttachment"
                testId="license-receipt"
                setFiles={() => clearFilesForKey("licenseReceiptAttachment")}
                removeFn={() => clearFilesForKey("licenseReceiptAttachment")}
                isLoading={Boolean(isUploading || isReceiptUploadInFlight)}
              />
            )}
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ReceiptUploadDrawer;
